import React, { type MouseEvent, useCallback } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import {
	MentionsIndicator,
	UNREAD_MENTIONS_INDICATOR_WRAPPER_TEST_ID,
} from '@atlassian/jira-mentions-indicator';
import type { Mentions } from '../../../../../model/types';

type MentionsIndicatorProps = {
	unreadMentions: Mentions | null;
	onClick: (
		withCmd: boolean,
		withShift: boolean,
		source: 'mouse' | 'keyboard',
		viaCheckbox?: boolean,
		externalAction?: ExternalAction,
	) => void;
};

export const MentionsIndicatorField = ({ unreadMentions, onClick }: MentionsIndicatorProps) => {
	const handleClick = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();

			if (e.defaultPrevented) {
				return;
			}

			const withCmd = e.ctrlKey || e.metaKey;
			const withShift = e.shiftKey;

			onClick(withCmd, withShift, 'mouse');
		},
		[onClick],
	);

	const onClickToGoToComment = useCallback(
		(e: MouseEvent | KeyboardEvent) => {
			e.stopPropagation();

			if (e.defaultPrevented) {
				return;
			}

			const withCmd = e.ctrlKey || e.metaKey;
			const withShift = e.shiftKey;

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			onClick(withCmd, withShift, e.type === 'keydown' ? 'keyboard' : 'mouse', false, {
				type: 'SCROLL_TO_COMMENT',
				payload: {
					commentId: unreadMentions?.latestUnreadCommentId,
				},
			} as ExternalAction);
		},
		[onClick, unreadMentions?.latestUnreadCommentId],
	);

	if (unreadMentions) {
		return unreadMentions?.latestUnreadCommentId ? (
			<Box xcss={mentionsIndicatorStyles} testId={UNREAD_MENTIONS_INDICATOR_WRAPPER_TEST_ID}>
				<MentionsIndicator onClick={onClickToGoToComment} />
			</Box>
		) : (
			<Box xcss={mentionsIndicatorStyles} testId={UNREAD_MENTIONS_INDICATOR_WRAPPER_TEST_ID}>
				<MentionsIndicator onClick={handleClick} />
			</Box>
		);
	}
	// Render empty div if there are no mentions, to keep elements in a card list aligned vertically
	return <Box xcss={mentionsIndicatorStyles} testId={UNREAD_MENTIONS_INDICATOR_WRAPPER_TEST_ID} />;
};

const mentionsIndicatorStyles = xcss({
	width: token('space.300', '1.5rem'),
});
